import { useState } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, ServerCog, Sparkles, Globe2, Workflow } from 'lucide-react';
import { DEFAULT_BASE_URL, discoverServer, type MfaChallenge } from '@dts/core';
import type { PublicSession } from '@/bridge/types';
import dtsMark from '@/dts-mark.svg';
import { PoweredBy } from '@/components/PoweredBy';
import { Button, Input } from '@/components/ui';
import { MfaStep } from '@/components/MfaStep';

/** Invitation links open the app at #invite?email=…&code=… (a fragment, so the code never reaches a server). */
function inviteFromLink(): { email: string; code: string } | null {
  const m = location.hash.match(/^#invite\?(.*)$/);
  if (!m) return null;
  const q = new URLSearchParams(m[1]);
  history.replaceState(null, '', location.pathname + location.search);
  return { email: q.get('email') ?? '', code: (q.get('code') ?? '').toUpperCase() };
}

// Read once at startup: the link is consumed (and removed from the address bar) on first read.
const linkInvite = inviteFromLink();

export function Login({ onLogin }: { onLogin: (s: PublicSession) => void }) {
  const invite = linkInvite;
  const [email, setEmail] = useState(invite?.email ?? '');
  const [password, setPassword] = useState('');
  const [server, setServer] = useState(() => localStorage.getItem('dts.server') ?? DEFAULT_BASE_URL);
  // A server saved from an earlier sign-in or typed in Server settings is kept; otherwise the email's domain may name one.
  const [serverChosen, setServerChosen] = useState(() => localStorage.getItem('dts.server') !== null);
  const [showServer, setShowServer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'login' | 'invite'>(invite ? 'invite' : 'login');
  const [code, setCode] = useState(invite?.code ?? '');
  const [confirm, setConfirm] = useState('');
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'invite' && password !== confirm) {
      setError("The passwords don't match.");
      return;
    }
    setBusy(true);
    setError(null);
    let target = server;
    if (!serverChosen && target === DEFAULT_BASE_URL) {
      const found = await discoverServer(email);
      if (found) setServer((target = found));
    }
    const r = mode === 'invite'
      ? await window.dts.auth.acceptInvite(target, email, code, password)
      : await window.dts.auth.login(target, email, password);
    setBusy(false);
    if (!r.ok) {
      setError(mode === 'login' && r.status === 401 ? 'That email and password did not match.' : r.error);
      return;
    }
    localStorage.setItem('dts.server', target);
    // Password accepted; two-step verification comes next.
    setPassword('');
    setConfirm('');
    setChallenge(r.data);
  };

  return (
    <div className="dossier-grid relative flex h-full">
      <div className="drag absolute inset-x-0 top-0 h-12" />
      <div className="relative hidden flex-1 flex-col justify-between overflow-hidden p-14 lg:flex">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
          <img src={dtsMark} alt="DTS" className="h-9 w-auto drop-shadow-[0_0_12px_rgba(0,212,255,0.4)]" draggable={false} />
          <span className="text-[17px] font-semibold tracking-tight">DTS Mail</span>
        </motion.div>
        <div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display text-[68px] leading-[0.98] tracking-tight">
            Email that <em className="ai-text">reads</em>,<br />writes & remembers<br />for you.
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-10 grid max-w-xl grid-cols-3 gap-4">
            {[
              { icon: <Sparkles />, t: 'AI-first', d: 'Briefs, summaries & drafts by AI' },
              { icon: <Globe2 />, t: 'Every domain', d: 'All your addresses, one inbox' },
              { icon: <Workflow />, t: 'CRM aware', d: 'Replies logged via MCP' },
            ].map((f) => (
              <div key={f.t} className="glass rounded-2xl p-4">
                <div className="mb-2 text-accent [&_svg]:size-5">{f.icon}</div>
                <div className="text-[13.5px] font-semibold">{f.t}</div>
                <div className="text-[12px] text-muted">{f.d}</div>
              </div>
            ))}
          </motion.div>
        </div>
        <div className="text-[12px] text-faint">Dynamic Tabletop Systems</div>
      </div>

      <div className="relative flex w-full items-center justify-center p-8 lg:w-[520px]">
        {challenge ? (
          <motion.div
            key="mfa"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="glass w-full max-w-[400px] rounded-3xl bg-raised/70 p-8 shadow-2xl"
          >
            <MfaStep server={server} challenge={challenge} onDone={onLogin} onCancel={() => (setChallenge(null), setMode('login'), setError(null))} />
          </motion.div>
        ) : (
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="glass w-full max-w-[380px] rounded-3xl bg-raised/70 p-8 shadow-2xl"
        >
          <div className="mb-6">
            <div className="text-[22px] font-semibold tracking-tight">{mode === 'invite' ? 'Accept your invitation' : 'Welcome back'}</div>
            <div className="text-[13px] text-muted">{mode === 'invite' ? 'Enter the code from your invitation and choose a password' : 'Sign in to your mailbox'}</div>
          </div>
          <div className="space-y-3">
            <Input type="email" autoFocus required placeholder="you@yourdomain.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" />
            {mode === 'invite' && (
              <Input required placeholder="Invite code (e.g. ABCD-EFGH)" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="h-11 font-mono tracking-widest" />
            )}
            <Input type="password" required minLength={mode === 'invite' ? 8 : undefined} placeholder={mode === 'invite' ? 'Choose a password (8+ characters)' : 'Password'} value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" />
            {mode === 'invite' && <Input type="password" required placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11" />}
            {showServer && <Input value={server} onChange={(e) => (setServer(e.target.value), setServerChosen(true))} placeholder="Server URL" className="h-10 font-mono text-[12px]" />}
          </div>
          {error && <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="mt-3 rounded-xl bg-danger/10 px-3 py-2 text-[12.5px] text-danger">{error}</motion.div>}
          <Button type="submit" variant="primary" size="lg" loading={busy} className="mt-5 w-full">
            {mode === 'invite' ? 'Set password & sign in' : 'Sign in'} <ArrowRight className="size-4" />
          </Button>
          <button type="button" onClick={() => (setMode(mode === 'login' ? 'invite' : 'login'), setError(null))} className="mx-auto mt-4 block text-[12.5px] font-medium text-accent hover:underline">
            {mode === 'login' ? 'I have an invite code' : 'I already have a password'}
          </button>
          <button type="button" onClick={() => setShowServer(!showServer)} className="mx-auto mt-4 flex items-center gap-1.5 text-[12px] text-faint hover:text-muted">
            <ServerCog className="size-3.5" /> {showServer ? 'Hide server' : 'Server settings'}
          </button>
        </motion.form>
        )}
      </div>
      <PoweredBy className="absolute inset-x-0 bottom-4 flex" />
    </div>
  );
}
